import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, NumberInput } from '@shared-ui/components';
import { adminApi, type ExperimentDefinition, type ExperimentVariant } from '../../api/adminApi';
import './FeatureGates.css';
import './Experiments.css';

const ALL_RINGS = ['devtesting', 'beta', 'prod', 'guest'] as const;
type Ring = typeof ALL_RINGS[number];

/** Experiment whose variant names are subscription ids (edit pre-fills the
 *  guest-subscription-* family and validates names against real definitions). */
const GUEST_SUBSCRIPTION_KEY = 'guest-subscription';

/** Experiment whose variant names are pipeline config names. Same idea as
 *  GUEST_SUBSCRIPTION_KEY — the arm IS the thing it names, so the editor offers
 *  the real list rather than letting a typo through as a silently dead arm. */
const PIPELINE_CONFIG_KEY = 'analysis-pipeline-config';

interface ExperimentEntry {
  key: string;
  enabled: boolean;
  description: string;
  enabledForRings: Ring[];
  variants: ExperimentVariant[];
}

/** Known keys + their canonical variants, so an admin can add a standard
 *  experiment without re-typing the arms. Keep in sync with
 *  Backend/functions/src/shared/experiments.ts EXPERIMENT_KEYS. */
const KNOWN_EXPERIMENTS: Record<string, { description: string; variants: ExperimentVariant[] }> = {
  'day1-modality': {
    description: 'Day-1 first-question modality',
    variants: [
      { name: 'control_solve', weight: 1 },
      { name: 'select', weight: 1 },
      { name: 'hybrid', weight: 1 },
    ],
  },
  'day1-subject': {
    description: 'Day-1 subject pick',
    variants: [
      { name: 'autopick', weight: 1 },
      { name: 'warmup_ask', weight: 1 },
      { name: 'chip_chooser', weight: 1 },
    ],
  },
  'day1-difficulty': {
    description: 'Day-1 first-question difficulty',
    variants: [
      { name: 'fixed_very_easy', weight: 1 },
      { name: 'comfort_driven', weight: 1 },
    ],
  },
  'day1-auth-gate': {
    description: 'Aha-before-signup. Absence/control = no anonymous; aha_first = offer guest entry',
    variants: [
      { name: 'register_first', weight: 1 },
      { name: 'aha_first', weight: 1 },
    ],
  },
  'guest-subscription': {
    description: 'Guest subscription at creation. Each variant name is a subscription id (edit pre-fills guest-subscription-*).',
    variants: [],
  },
  'analysis-pipeline-config': {
    description: 'Which pipeline config a user runs. Each variant name is a pipeline config name. Scope an arm with its own rings to roll out one ring at a time.',
    variants: [],
  },
  'day2-nudge': {
    description: 'Stage-2 re-engagement: inapp_only (control) | inapp_plus_outbound (WhatsApp)',
    variants: [
      { name: 'inapp_only', weight: 1 },
      { name: 'inapp_plus_outbound', weight: 1 },
    ],
  },
  'student-app-flow': {
    description: 'Student-app flow/IA: base (current) | design1-fre-cta (new goal-driven 5-tab redesign)',
    variants: [
      { name: 'base', weight: 1 },
      { name: 'design1-fre-cta', weight: 0 },
    ],
  },
};

function splitPercent(variants: ExperimentVariant[]): Record<string, number> {
  const total = variants.reduce((s, v) => s + (v.weight > 0 ? v.weight : 0), 0);
  const out: Record<string, number> = {};
  for (const v of variants) {
    out[v.name] = total > 0 && v.weight > 0 ? Math.round((v.weight / total) * 100) : 0;
  }
  return out;
}

function cloneEntry(e: ExperimentEntry): ExperimentEntry {
  return { ...e, enabledForRings: [...e.enabledForRings], variants: e.variants.map((v) => ({ ...v })) };
}

export function Experiments() {
  const queryClient = useQueryClient();
  const [experiments, setExperiments] = useState<ExperimentEntry[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  // Dialog edit state: a draft copy of one experiment. idx === -1 ⇒ creating new.
  const [editing, setEditing] = useState<{ idx: number; draft: ExperimentEntry } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['experiments'],
    queryFn: adminApi.getExperiments,
  });

  // The guest-subscription experiment's variant names are subscription ids: the
  // edit dialog pre-fills the guest-subscription-* ones and validates every
  // name against the real definitions.
  const { data: subscriptionDefs = [] } = useQuery({
    queryKey: ['subscription-definitions'],
    queryFn: adminApi.getSubscriptionDefinitions,
  });
  const subscriptionIds = useMemo(() => subscriptionDefs.map((s) => s.id), [subscriptionDefs]);

  // The analysis-pipeline-config experiment's variant names are pipeline config
  // names — offered as a list for the same reason as subscriptions above.
  const { data: pipelineConfigList } = useQuery({
    queryKey: ['pipeline-configs'],
    queryFn: adminApi.listPipelineConfigs,
  });
  const pipelineConfigNames = useMemo(
    () => (pipelineConfigList?.configs ?? []).map((c) => c.name).sort(),
    [pipelineConfigList],
  );
  const guestSubscriptionIds = useMemo(
    () => subscriptionIds.filter((id) => id.startsWith(`${GUEST_SUBSCRIPTION_KEY}-`)).sort(),
    [subscriptionIds],
  );
  // Add any guest-subscription-* subscription not already a variant (weight 1).
  const withGuestSubscriptionPrefill = (draft: ExperimentEntry): ExperimentEntry => {
    if (draft.key !== GUEST_SUBSCRIPTION_KEY) return draft;
    const have = new Set(draft.variants.map((v) => v.name));
    return {
      ...draft,
      variants: [...draft.variants, ...guestSubscriptionIds.filter((id) => !have.has(id)).map((id) => ({ name: id, weight: 1 }))],
    };
  };

  useEffect(() => {
    if (data?.experiments) {
      const entries = Object.entries(data.experiments).map(([key, def]) => ({
        key,
        enabled: def.enabled,
        description: def.description || '',
        enabledForRings: (def.enabledForRings || []) as Ring[],
        variants: (def.variants || []).map((v) => ({
          name: v.name,
          weight: v.weight,
          ...(v.enabledForRings?.length ? { enabledForRings: v.enabledForRings } : {}),
        })),
      }));
      setExperiments(entries);
    }
  }, [data]);

  const toRecord = (entries: ExperimentEntry[]): Record<string, ExperimentDefinition> => {
    const record: Record<string, ExperimentDefinition> = {};
    for (const e of entries) {
      if (!e.key.trim()) continue;
      record[e.key.trim()] = {
        enabled: e.enabled,
        ...(e.description ? { description: e.description } : {}),
        ...(e.enabledForRings.length > 0 ? { enabledForRings: e.enabledForRings } : {}),
        variants: e.variants
          .filter((v) => v.name.trim())
          .map((v) => ({
            name: v.name.trim(),
            weight: Number(v.weight) || 0,
            ...(v.enabledForRings?.length ? { enabledForRings: v.enabledForRings } : {}),
          })),
      };
    }
    return record;
  };

  // Single persistence path. Every mutating action (toggle, delete, dialog save)
  // builds the full next list and saves it — there is no global Save button.
  const saveMutation = useMutation({
    mutationFn: (entries: ExperimentEntry[]) => adminApi.updateExperiments(toRecord(entries)),
    onMutate: () => setSaveStatus('saving'),
    onSuccess: (_res, entries) => {
      setExperiments(entries);
      setEditing(null);
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['experiments'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  // Quick card actions (persist immediately — they aren't part of dialog editing).
  const toggleEnabled = (idx: number, enabled: boolean) =>
    saveMutation.mutate(experiments.map((e, i) => (i === idx ? { ...e, enabled } : e)));
  const deleteExperiment = (idx: number) =>
    saveMutation.mutate(experiments.filter((_, i) => i !== idx));

  // Dialog (draft) editing.
  const openEdit = (idx: number) => setEditing({ idx, draft: withGuestSubscriptionPrefill(cloneEntry(experiments[idx])) });
  const openNew = (draft: ExperimentEntry) => setEditing({ idx: -1, draft: withGuestSubscriptionPrefill(draft) });
  const closeDialog = () => setEditing(null);
  const updateDraft = (u: Partial<ExperimentEntry>) =>
    setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...u } } : e));
  const updateDraftVariant = (vIdx: number, u: Partial<ExperimentVariant>) =>
    setEditing((e) => {
      if (!e) return e;
      const variants = e.draft.variants.map((v, i) => (i === vIdx ? { ...v, ...u } : v));
      return { ...e, draft: { ...e.draft, variants } };
    });
  const addDraftVariant = () =>
    setEditing((e) => (e ? { ...e, draft: { ...e.draft, variants: [...e.draft.variants, { name: '', weight: 1 }] } } : e));
  const removeDraftVariant = (vIdx: number) =>
    setEditing((e) => (e ? { ...e, draft: { ...e.draft, variants: e.draft.variants.filter((_, i) => i !== vIdx) } } : e));
  const saveDraft = () => {
    if (!editing) return;
    const next =
      editing.idx === -1
        ? [...experiments, editing.draft]
        : experiments.map((e, i) => (i === editing.idx ? editing.draft : e));
    saveMutation.mutate(next);
  };

  const existingKeys = new Set(experiments.map((e) => e.key));
  const missingKnown = Object.keys(KNOWN_EXPERIMENTS).filter((k) => !existingKeys.has(k));

  const addKnown = (key: string) => {
    const def = KNOWN_EXPERIMENTS[key];
    openNew({ key, enabled: false, description: def.description, enabledForRings: [], variants: def.variants.map((v) => ({ ...v })) });
  };
  const addExperiment = () =>
    openNew({ key: '', enabled: false, description: '', enabledForRings: [], variants: [{ name: '', weight: 1 }] });

  // A new draft's key is free-text; an existing experiment's key is immutable.
  const draftKeyLocked = !!editing && editing.idx !== -1;
  const draftPct = editing ? splitPercent(editing.draft.variants) : {};
  // splitPercent divides over every variant, but per-variant rings mean the real
  // share depends on which arms a given ring can see. Flag that rather than show
  // a number that's only right for a ring with no exclusions.
  const draftHasVariantRings = !!editing?.draft.variants.some((v) => (v.enabledForRings?.length ?? 0) > 0);

  // Some experiments' variant names ARE identifiers of something else — a
  // subscription id, a pipeline config name. For those the editor offers the
  // real list and blocks Save on anything outside it, since a typo would
  // otherwise persist as an arm that can never resolve to anything.
  const nameCatalogue: { ids: string[]; noun: string } | null = useMemo(() => {
    if (editing?.draft.key === GUEST_SUBSCRIPTION_KEY) return { ids: subscriptionIds, noun: 'subscription id' };
    if (editing?.draft.key === PIPELINE_CONFIG_KEY) return { ids: pipelineConfigNames, noun: 'pipeline config' };
    return null;
  }, [editing?.draft.key, subscriptionIds, pipelineConfigNames]);

  const isInvalidVariantName = (name: string) =>
    // An empty catalogue means the list hasn't loaded — don't flag everything.
    !!nameCatalogue && nameCatalogue.ids.length > 0
    && name.trim() !== '' && !nameCatalogue.ids.includes(name.trim());
  const invalidVariantNames = editing
    ? editing.draft.variants.filter((v) => isInvalidVariantName(v.name)).map((v) => v.name)
    : [];

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Experiments</h2>
        <p className="section-description">
          A/B variant assignment. Users are bucketed deterministically by their id; weights are
          relative (need not sum to 100). When an experiment is off — or a user's ring isn't in the
          rollout — no variant is assigned, and consumers fall back to control behaviour.
        </p>
      </div>

      {isLoading ? (
        <div className="loading">Loading experiments...</div>
      ) : (
        <>
          {experiments.length === 0 && <p className="fg-empty">No experiments defined yet.</p>}

          <div className="fg-list">
            {experiments.map((exp, idx) => {
              const pct = splitPercent(exp.variants);
              return (
                <div key={idx} className="fg-card">
                  <div className="fg-card-top">
                    <div className="fg-toggle-area">
                      <label className="fg-switch">
                        <input
                          type="checkbox"
                          checked={exp.enabled}
                          onChange={(e) => toggleEnabled(idx, e.target.checked)}
                          disabled={saveMutation.isPending}
                        />
                        <span className="fg-slider" />
                      </label>
                      <span className={`fg-status ${exp.enabled ? 'fg-on' : 'fg-off'}`}>
                        {exp.enabled ? 'ON' : 'OFF'}
                      </span>
                    </div>
                    <div className="exp-card-actions">
                      <button className="exp-edit-btn" onClick={() => openEdit(idx)}>Edit</button>
                      <button
                        className="exp-delete-btn"
                        onClick={() => deleteExperiment(idx)}
                        disabled={saveMutation.isPending}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="fg-fields">
                    <div className="fg-field">
                      <label className="feature-field-label">Key (unique)</label>
                      <div className="exp-readonly-value">{exp.key}</div>
                    </div>
                    <div className="fg-field fg-field-desc">
                      <label className="feature-field-label">Description</label>
                      <div className="exp-readonly-value">{exp.description || <span className="exp-muted">—</span>}</div>
                    </div>
                  </div>

                  <div className="exp-variants">
                    <label className="feature-field-label">Variants (name + relative weight)</label>
                    {exp.variants.map((v, vIdx) => (
                      <div key={vIdx} className="exp-variant-row">
                        <span className="exp-variant-name-label">{v.name || <span className="exp-muted">unnamed</span>}</span>
                        <span className="exp-variant-weight-label">{v.weight}</span>
                        {/* Shown only when the arm is restricted. Unscoped arms are
                            the common case and labelling every one "all rings" would
                            bury the ones that actually differ. The percentage is a
                            share of the whole, so it over-states a scoped arm for
                            rings that can't see it — hence rendering it muted with
                            the rings that qualify it. */}
                        {(v.enabledForRings?.length ?? 0) > 0 ? (
                          <span className="exp-variant-pct exp-variant-pct-scoped">
                            {pct[v.name] ?? 0}%
                          </span>
                        ) : (
                          <span className="exp-variant-pct">{pct[v.name] ?? 0}%</span>
                        )}
                        {(v.enabledForRings?.length ?? 0) > 0 && (
                          <span className="exp-variant-rings-tag">
                            {v.enabledForRings!.join(', ')} only
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {exp.enabled && (
                    <div className="fg-rings">
                      <label className="feature-field-label">Rollout rings</label>
                      <span className="fg-rings-hint">
                        {exp.enabledForRings.length === 0 ? '(all rings)' : exp.enabledForRings.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {missingKnown.length > 0 && (
            <div className="exp-known">
              <span className="exp-known-label">Add a known experiment:</span>
              {missingKnown.map((k) => (
                <button key={k} className="exp-known-btn" onClick={() => addKnown(k)}>
                  + {k}
                </button>
              ))}
            </div>
          )}

          <div className="feature-actions">
            <button className="feature-add-btn" onClick={addExperiment}>
              + Add Experiment
            </button>
          </div>
          {saveStatus === 'saved' && <span className="status-saved feature-status">Experiments saved!</span>}
          {saveStatus === 'error' && <span className="status-error feature-status">{saveError || 'Failed to save'}</span>}
        </>
      )}

      {editing && (
        <Modal
          isOpen
          onClose={closeDialog}
          title={editing.idx === -1 ? 'New experiment' : `Edit ${editing.draft.key || 'experiment'}`}
        >
          <div className="exp-dialog">
            <div className="fg-field">
              <label className="feature-field-label">Key (unique)</label>
              <input
                type="text"
                className="feature-field-input"
                value={editing.draft.key}
                onChange={(e) => updateDraft({ key: e.target.value })}
                placeholder="e.g. day1-auth-gate"
                readOnly={draftKeyLocked}
              />
            </div>
            <div className="fg-field">
              <label className="feature-field-label">Description</label>
              <input
                type="text"
                className="feature-field-input"
                value={editing.draft.description}
                onChange={(e) => updateDraft({ description: e.target.value })}
                placeholder="What this experiment tests"
              />
            </div>

            <div className="exp-variants">
              <label className="feature-field-label">
                Variants (name + relative weight)
                {nameCatalogue && <span className="exp-variant-hint"> — names must be a {nameCatalogue.noun}</span>}
                {draftHasVariantRings && (
                  <span className="exp-variant-hint"> — percentages assume all arms available; a ring that excludes an arm splits over the rest</span>
                )}
              </label>
              {nameCatalogue && (
                <datalist id="exp-variant-name-options">
                  {nameCatalogue.ids.map((id) => <option key={id} value={id} />)}
                </datalist>
              )}
              {editing.draft.variants.map((v, vIdx) => (
                <div key={vIdx} className="exp-variant-block">
                  <div className="exp-variant-row">
                    <input
                      type="text"
                      className={`feature-field-input exp-variant-name${isInvalidVariantName(v.name) ? ' exp-variant-invalid' : ''}`}
                      value={v.name}
                      onChange={(e) => updateDraftVariant(vIdx, { name: e.target.value })}
                      placeholder={nameCatalogue ? nameCatalogue.noun : 'variant name'}
                      list={nameCatalogue ? 'exp-variant-name-options' : undefined}
                    />
                    <NumberInput
                      min={0}
                      className="feature-field-input exp-variant-weight"
                      value={v.weight}
                      onChange={(w) => { if (w != null) updateDraftVariant(vIdx, { weight: w }); }}
                    />
                    <span className="exp-variant-pct">{draftPct[v.name] ?? 0}%</span>
                    <button
                      className="feature-remove-btn exp-variant-remove"
                      onClick={() => removeDraftVariant(vIdx)}
                      title="Remove variant"
                    >
                      &times;
                    </button>
                  </div>
                  {/* Per-variant rings. Narrower than the experiment-level rings
                      below and ANDed with them — this is what lets one arm roll
                      out ring by ring without disturbing the others. */}
                  <div className="exp-variant-rings">
                    <span className="exp-variant-rings-label">
                      Rings{(v.enabledForRings?.length ?? 0) === 0 ? ' (all)' : ''}
                    </span>
                    {ALL_RINGS.map((ring) => (
                      <label key={ring} className="fg-ring-label">
                        <input
                          type="checkbox"
                          checked={(v.enabledForRings ?? []).includes(ring)}
                          onChange={(e) => {
                            const current = v.enabledForRings ?? [];
                            const next = e.target.checked
                              ? [...current, ring]
                              : current.filter((r) => r !== ring);
                            // Drop the key entirely when empty so an untouched
                            // variant serializes exactly as it did before.
                            updateDraftVariant(vIdx, { enabledForRings: next.length > 0 ? next : undefined });
                          }}
                        />
                        {ring}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {invalidVariantNames.length > 0 && (
                <span className="status-error feature-status">
                  Not a known {nameCatalogue?.noun}: {invalidVariantNames.join(', ')}
                </span>
              )}
              <button className="exp-add-variant-btn" onClick={addDraftVariant}>
                + Add Variant
              </button>
            </div>

            <div className="fg-rings">
              <label className="feature-field-label">Rollout rings</label>
              <span className="fg-rings-hint">{editing.draft.enabledForRings.length === 0 ? '(all rings)' : ''}</span>
              <div className="fg-ring-checks">
                {ALL_RINGS.map((ring) => (
                  <label key={ring} className="fg-ring-label">
                    <input
                      type="checkbox"
                      checked={editing.draft.enabledForRings.includes(ring)}
                      onChange={(e) =>
                        updateDraft({
                          enabledForRings: e.target.checked
                            ? [...editing.draft.enabledForRings, ring]
                            : editing.draft.enabledForRings.filter((r) => r !== ring),
                        })
                      }
                    />
                    {ring}
                  </label>
                ))}
              </div>
            </div>

            {saveStatus === 'error' && <span className="status-error feature-status">{saveError || 'Failed to save'}</span>}
            <div className="exp-dialog-actions">
              <button className="btn-secondary" onClick={closeDialog} disabled={saveMutation.isPending}>
                Cancel
              </button>
              <button
                className="feature-save-btn"
                onClick={saveDraft}
                disabled={saveMutation.isPending || !editing.draft.key.trim() || invalidVariantNames.length > 0}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
